/**
 * TagInput — chip-style multi-tag input for transactions.
 *
 * Renders chips for each tag in `value`, plus a single text input. Typing
 * + Enter / comma commits the buffer as a new chip. Backspace at an
 * empty input removes the most recent chip. A second row below shows
 * `suggestions` (typically the user's existing tags from past tx) as
 * tap-to-add chips, with already-chosen ones filtered out.
 *
 * Sharp edges:
 *   - Every chip is rendered via `{tag}` (text), NEVER through
 *     `dangerouslySetInnerHTML` — XSS-safe even if a malicious value
 *     contains `<script>`.
 *   - Empty / whitespace / duplicate chips are silently dropped at
 *     `addChip` time.
 *   - The `#` prefix is part of the stored value (sanitiseTag enforces
 *     exactly one). Calling `sanitizeTag` here means callers don't need
 *     to think about it.
 */
import { useCallback, useState, type KeyboardEvent } from 'react';
import { sanitizeTag } from '@/lib/tags';

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  /** Optional placeholder for the underlying text input. */
  placeholder?: string;
  /** Controlled buffer for the unfinished text the user is typing.
   *  Lifted out of TagInput so the parent (AddTxScreen) can use it to
   *  filter the 常用標籤 suggestion row in real time. Optional — when
   *  not provided, TagInput keeps its own internal state. */
  buffer?: string;
  onBufferChange?: (next: string) => void;
}

export default function TagInput({
  value,
  onChange,
  suggestions,
  placeholder,
  buffer: bufferProp,
  onBufferChange,
}: TagInputProps): JSX.Element {
  const [bufferLocal, setBufferLocal] = useState('');
  const buffer = bufferProp ?? bufferLocal;
  const setBuffer = (next: string): void => {
    if (onBufferChange) onBufferChange(next);
    else setBufferLocal(next);
  };

  const addChip = useCallback(
    (raw: string): boolean => {
      const tag = sanitizeTag(raw);
      if (tag == null) return false;
      if (value.includes(tag)) return false;
      onChange([...value, tag]);
      return true;
    },
    [value, onChange],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        if (addChip(buffer)) setBuffer('');
        return;
      }
      if (e.key === 'Backspace' && buffer === '' && value.length > 0) {
        e.preventDefault();
        onChange(value.slice(0, -1));
      }
    },
    [addChip, buffer, value, onChange],
  );

  const remaining = suggestions.filter((s) => {
    const tag = sanitizeTag(s);
    return tag != null && !value.includes(tag);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          background: 'var(--bg-surface)',
          border: '0.5px solid var(--border-1)',
          borderRadius: 999,
          minHeight: 28,
          font: '400 12px var(--font-cjk)',
          color: 'var(--fg-1)',
        }}
      >
        {value.map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '2px 8px',
              background: 'var(--honey-500)',
              color: '#fff',
              borderRadius: 999,
              font: '500 11px var(--font-cjk)',
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {tag}
            <button
              type="button"
              aria-label={`移除 ${tag}`}
              onClick={() => onChange(value.filter((t) => t !== tag))}
              style={{
                background: 'transparent',
                border: 0,
                color: '#fff',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
                font: 'inherit',
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          aria-label="標籤"
          placeholder={placeholder ?? '#標籤'}
          value={buffer}
          onChange={(e) => setBuffer(e.target.value)}
          onKeyDown={onKeyDown}
          style={{
            border: 0,
            background: 'transparent',
            font: 'inherit',
            color: 'inherit',
            outline: 'none',
            padding: 0,
            flex: 1,
            minWidth: 60,
          }}
        />
      </div>
      {remaining.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          {remaining.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addChip(s)}
              style={{
                padding: '2px 8px',
                background: 'var(--bg-raised)',
                color: 'var(--fg-2)',
                border: '0.5px solid var(--border-1)',
                borderRadius: 999,
                font: '500 11px var(--font-cjk)',
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
